import { useState, useEffect, useContext } from "react";
import { PumpContext } from "../context/PumpContext";
import "./PumpFaceplate.css";
import graphImg from "../assets/graph.jpg";

function PumpFaceplate({
  tag,
  close,
  defaultPosition = { x: 300, y: 120 },
  baseRPM = 1800,
  transmitters,
  valveData
}) {

  // =========================
  // DRAGGING
  // =========================
  const [position, setPosition] = useState(defaultPosition);
  const [dragging, setDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const ctx = useContext(PumpContext);

  const pumpData = ctx?.pumpData;
  const updatePump = ctx?.updatePump;

  const data = pumpData?.[tag] ?? {
    speed: 0,
    running: false,
    bladeCount: 3,
    size: 80
  };

  const speed = Number(data.speed || 0);
  const running = Boolean(data.running);
  const bladeCount = Number(data.bladeCount || 3);
  const size = Number(data.size || 80);

  const rpm = running ? (speed / 100) * baseRPM : 0;

  // =========================
  // INPUTS FROM PROCESS
  // =========================
  const inletFlow = Number(transmitters?.FIC_2808 ?? 0);
  const inletPressure = Number(transmitters?.PT_2804 ?? 0);
  const inletTemp = Number(transmitters?.TT_2802 ?? 0);

  const valveOpening = Number(valveData?.FCV_2805?.opening || 0);

  // =========================
  // OUTPUT STATES
  // =========================
  const [flow, setFlow] = useState(0);
  const [pressure, setPressure] = useState(0);
  const [temp, setTemp] = useState(0);

  
  // =========================
  // PUMP LOGIC
  // =========================
  useEffect(() => {
    if (!running) {
      setFlow(0);
      setPressure(inletPressure);
      setTemp(inletTemp);
      return;
    }

    const interval = setInterval(() => {

      // FLOW
      const newFlow = inletFlow * (valveOpening / 100);

      // PRESSURE LOGIC
      let newPressure = inletPressure;

      if (valveOpening > 0) {
        newPressure = inletPressure + (1 - valveOpening / 100) * 0.8;
      } else {
        newPressure = Math.min(pressure + 0.05, 1.2); // DEADHEAD RISE
      }

      // TEMPERATURE LOGIC
      let newTemp = inletTemp;

      if (valveOpening > 0) {
        newTemp = inletTemp + (1 - valveOpening / 100) * 20;
      } else {
        newTemp = Math.min(temp + 1.5, 70); // DEADHEAD HEAT UP
      }

      // LIMITS
      newPressure = Math.min(newPressure, 1.2);
      newTemp = Math.min(newTemp, 70);

      setFlow(newFlow);
      setPressure(newPressure);
      setTemp(newTemp);

    }, 1000);

    return () => clearInterval(interval);

  }, [running, inletFlow, inletPressure, inletTemp, valveOpening, pressure, temp]);

  // =========================
  // DRAG EFFECT
  // =========================
  useEffect(() => {
    const move = (e) => {
      if (!dragging) return;

      setPosition({
        x: e.clientX - offset.x,
        y: e.clientY - offset.y
      });
    };

    const stop = () => setDragging(false);

    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", stop);

    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", stop);
    };
  }, [dragging, offset]);

  if (!ctx || !tag) return null;

  // =========================
  // HANDLERS
  // =========================
  const handleMouseDown = (e) => {
    setDragging(true);
    setOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  // =========================
  // UI
  // =========================
  return (
    <div
      className="ac-faceplate"
      style={{
        position: "absolute",
        left: position.x,
        top: position.y,
        zIndex: 999
      }}
    >

      {/* HEADER */}
      <div className="ac-header" onMouseDown={handleMouseDown}>
        <span className="ac-title">{tag}</span>
        <button className="ac-close-btn" onClick={close}>✖</button>
      </div>

      {/* BODY */}
      <div className="ac-body">

        <div className="ac-row">
          <label>RPM</label>
          <input className="ac-input" value={rpm.toFixed(0)} readOnly />
        </div>

        <div className="ac-row">
          <label>SPEED %</label>
          <input
            className="ac-input"
            type="number"
            value={speed}
            onChange={(e) =>
              updatePump(tag, { speed: Number(e.target.value) })
            }
          />
        </div>

        <div className="ac-row">
          <label>FLOW</label>
          <input className="ac-input" value={flow.toFixed(2)} readOnly />
        </div>

        <div className="ac-row">
          <label>PRESSURE</label>
          <input className="ac-input" value={pressure.toFixed(2)} readOnly />
        </div>

        <div className="ac-row">
          <label>TEMP</label>
          <input className="ac-input" value={temp.toFixed(1)} readOnly />
        </div>

        <div className="ac-row">
          <label>BLADES</label>
          <input
            className="ac-input"
            type="number"
            value={bladeCount}
            onChange={(e) =>
              updatePump(tag, { bladeCount: Number(e.target.value) })
            }
          />
        </div>

        <div className="ac-row">
          <label>SIZE</label>
          <input
            className="ac-input"
            type="number"
            value={size}
            onChange={(e) =>
              updatePump(tag, { size: Number(e.target.value) })
            }
          />
        </div>

      </div>

      {/* ACTIONS */}
      <div className="ac-actions">

        <button
          className={`ac-btn ac-start-btn ${running ? "active-start" : "inactive"}`}
          onClick={() => updatePump(tag, { running: true })}
        >
          START
        </button>

        <button
          className={`ac-btn ac-stop-btn ${!running ? "active-stop" : "inactive"}`}
          onClick={() => updatePump(tag, { running: false })}
        >
          STOP
        </button>

        <div className="ac-graph">
          <div className="ac-graph-inner">
            <img src={graphImg} alt="Pump Graph" />
            <div className="ac-graph-pointer" />
          </div>
        </div>

      </div>
    </div>
  );
}

export default PumpFaceplate;